import { createRouter, createWebHistory } from 'vue-router'
import { useUserStore } from '@/stores/userStore'
import HomeView from '@/views/HomeView.vue'
import TravelHome from '@/views/user/travel/TravelHomeView.vue'
import ProfileView from '@/views/user/ProfileView.vue'
import LoginView from '@/views/LoginView.vue'
import AdminHomeView from '@/views/admin/AdminHomeView.vue'
import AdminTravelEditView from '@/views/admin/travel/AdminTravelEditView.vue'
import BoardTravelView from '@/views/board/BoardTravelView.vue'
import AdminTravelExportView from '@/views/admin/export/AdminTravelExportView.vue'
import AdminParticipantsEditView from '@/views/admin/participants/AdminParticipantsEditView.vue'
import AdminParticipantEditView from '@/views/admin/participants/AdminParticipantEditView.vue'
import AdminUsersView from '@/views/admin/users/AdminUsersView.vue'
import TravelSummary from '@/views/user/travel/TravelSummaryView.vue'
import TravelRegistration from '@/views/user/travel/TravelRegistrationView.vue'
import TravelOptout from '@/views/user/travel/TravelRefusalView.vue'
import TravelRoadbook from '@/views/user/travel/TravelRoadbookView.vue'
import TravelPages from '@/views/user/travel/TravelPagesView.vue'
import VimeoPlayerView from '@/views/user/VimeoPlayerView.vue'
import AdminSettingsView from '@/views/admin/settings/AdminSettingsView.vue'
import travelApi from '@/api/travelApi'
import userApi from '@/api/userApi'
import adminApi from '@/api/adminApi'

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    {
      path: '/',
      name: 'home',
      component: HomeView,
    },
    {
      path: '/login/:magic',
      name: 'login-link',
      component: HomeView,
      beforeEnter: async (to) => {
          await userApi.getMeFromLoginLink(to.params.magic as string).then((response) => {;
          if (response.status === 200 && response.data) {
            useUserStore().setCurrentUser(response.data)
          }
        }).catch(() => {});
        return { name: 'home' };
      },
    },
    {
      path: '/login',
      name: 'login',
      component: LoginView,
    },
    {
      path: '/profil',
      name: 'profile',
      component: ProfileView,
    },
    {
      path: '/invitation/:magic',
      name: 'invitation-link',
      component: TravelHome,
      beforeEnter: async (to) => {
        const response = await travelApi.validateInvitation(to.params.magic as string);
        if (response.status === 200 && response.data) {
          const travelId = response.data.travelId;
          to.params.id = travelId.toString();
          useUserStore().setConnectedUser(response.data.user);
          return { name: 'travel', params: { id: travelId } };
        }
        else {
          console.warn('Could not decode travel ID from magic link');
          return { name: 'home' };
        }
      },
    },
    {
      path: '/:id(\\d+)/recap',
      name: 'travel-summary',
      component: TravelSummary,
      props: route => ({ id: Number(route.params.id) }),
      beforeEnter: (to) => {
        const travelId = to.params.id;
        if (isNaN(Number(travelId))) {
          return { name: 'home' }; // Redirect to home if the ID is not a number
        }
        return true; // Proceed to the travel view
      }
    },
    {
      path: '/:slug/recap',
      name: 'travel-summary-slug',
      component: TravelSummary,
      props: true
    },
    {
      path: '/:id(\\d+)',
      name: 'travel',
      component: TravelHome,
      props: route => ({ id: Number(route.params.id) }),
      beforeEnter: (to) => {
        const travelId = to.params.id;
        if (isNaN(Number(travelId))) {
          return { name: 'home' }; // Redirect to home if the ID is not a number
        }
        return true; // Proceed to the travel view
      }
    },
    {
      path: '/:slug',
      name: 'travel-slug',
      component: TravelHome,
      props: true
    },
    {
      path: '/:id(\\d+)/inscription',
      name: 'travel-registration',
      component: TravelRegistration,
      props: route => ({ id: Number(route.params.id) }),
      beforeEnter: (to) => {
        const travelId = to.params.id;
        if (isNaN(Number(travelId))) {
          return { name: 'home' }; // Redirect to home if the ID is not a number
        }
        return true; // Proceed to the travel view
      }
    },
    {
      path: '/:slug/inscription',
      name: 'travel-registration-slug',
      component: TravelRegistration,
      props: true
    },
    {
      path: '/:id(\\d+)/optout',
      name: 'travel-optout',
      component: TravelOptout,
      props: route => ({ id: Number(route.params.id) }),
      beforeEnter: (to) => {
        const travelId = to.params.id;
        if (isNaN(Number(travelId))) {
          return { name: 'home' }; // Redirect to home if the ID is not a number
        }
        return true; // Proceed to the travel view
      }
    },
    {
      path: '/:slug/optout',
      name: 'travel-optout-slug',
      component: TravelOptout,
      props: true
    },
    {
      path: '/:id(\\d+)/carnet',
      name: 'travel-roadbook',
      component: TravelRoadbook,
      props: route => ({ id: Number(route.params.id) }),
      beforeEnter: (to) => {
        const travelId = to.params.id;
        if (isNaN(Number(travelId))) {
          return { name: 'home' };
        }
        return true;
      }
    },
    {
      path: '/:videoKey/video',
      name: 'vimeo-video',
      component: VimeoPlayerView,
      props: route => ({ videoKey: route.params.videoKey, title: route.query.title })
    },
    {
      path: '/:slug/carnet',
      name: 'travel-roadbook-slug',
      component: TravelRoadbook,
      props: true
    },
    {
      path: '/:slug/infos-pratiques',
      name: 'travel-pages-slug',
      component: TravelPages,
      props: true
    },
    {
      path: '/admin',
      name: 'admin',
      component: AdminHomeView,
    },
    {
      path: '/admin/travel',
      name: 'admin-new-travel',
      component: AdminTravelEditView
    },
    {
      path: '/admin/travel/:id(\\d+)',
      name: 'admin-edit-travel',
      component: AdminTravelEditView,
      props: route => ({ id: Number(route.params.id) }),
      beforeEnter: (to) => {
        const travelId = to.params.id;
        if (isNaN(Number(travelId))) {
          return { name: 'admin' }; // Redirect to admin home if the ID is not a number
        }
        return true;
      }
    },
    {
      path: '/admin/travel/:id(\\d+)/participants',
      name: 'admin-edit-travel-participants',
      component: AdminParticipantsEditView,
      props: route => ({ id: Number(route.params.id) }),
      beforeEnter: (to) => {
        const travelId = to.params.id;
        if (isNaN(Number(travelId))) {
          return { name: 'admin' }; // Redirect to admin home if the ID is not a number
        }
        return true;
      }
    },
    {
      path: '/admin/participant',
      name: 'admin-new-participant',
      component: AdminParticipantEditView,
      props: route => ({ travelId: route.query.travelId ? Number(route.query.travelId) : undefined })
    },
    {
      path: '/admin/participant/:id(\\d+)',
      name: 'admin-edit-participant',
      component: AdminParticipantEditView,
      props: route => ({ id: Number(route.params.id), travelId: route.query.travelId ? Number(route.query.travelId) : undefined }),
      beforeEnter: (to) => {
        const participantId = to.params.id;
        if (isNaN(Number(participantId))) {
          return { name: 'admin' }; // Redirect to admin home if the ID is not a number
        }
        return true;
      }
    },
    {
      path: '/board',
      name: 'travel-board',
      component: BoardTravelView,
      beforeEnter: async () => {
        const response = await adminApi.getTravels();
        if (response.status === 200 && response.data) {
          const activeTravels = response.data.items
            .filter(t => t.registrationEndDate > new Date() && t.registrationStartDate < new Date())
            .sort((a, b) => b.registrationStartDate.getTime() - a.registrationStartDate.getTime());

            if (activeTravels.length > 0) {
            return { name: 'travel-board-by-id', params: { id: activeTravels[0].id } };
          }
        }

        return { name: 'admin' }; // Redirect to admin if  recent no board found
      }
    },
    {
      path: '/board/:id(\\d+)',
      name: 'travel-board-by-id',
      component: BoardTravelView,
      props: route => ({ id: Number(route.params.id) }),
      beforeEnter: (to) => {
        const travelId = to.params.id;
        if (isNaN(Number(travelId))) {
          return { name: 'home' }; // Redirect to home if the ID is not a number
        }
        return true;
      }
    },
    {
      path: '/board/:slug',
      name: 'travel-board-by-slug',
      component: BoardTravelView,
      props: true
    },
    {
      path: '/admin/travel/:id(\\d+)/export',
      name: 'admin-travel-export',
      component: AdminTravelExportView,
      props: route => ({ id: Number(route.params.id) }),
      beforeEnter: (to) => {
        const travelId = to.params.id;
        if (isNaN(Number(travelId))) {
          return { name: 'admin' }; // Redirect to admin home if the ID is not a number
        }
        return true;
      }
    },
    {
      path: '/admin/users',
      name: 'admin-users',
      component: AdminUsersView,
    },
    {
      path: '/admin/settings',
      name: 'admin-settings',
      component: AdminSettingsView,
    },
    // Fallback route for 404 Not Found
    {
      name: 'not-found',
      path: '/:pathMatch(.*)*',
      component: HomeView
    }
  ],
})

router.beforeEach((to) => {
  // if querystring impersonatedUserId is set, store it in sessionStorage and use it for API calls.
  // It is used for administrators to impersonate users.
  // The sessionStorage is used to store the impersonatedUserId for the duration of the session, and it is sent in the headers of API calls to identify the impersonated user.
  const queryImpersonatedUserId = to.query.impersonatedUserId as string | undefined;
  if (queryImpersonatedUserId) {
    sessionStorage.setItem('MyNemo.impersonatedUserId', queryImpersonatedUserId);
    const newQuery = { ...to.query };
    delete newQuery.impersonatedUserId;
    return { ...to, query: newQuery };
  }
});

export default router
